// WB-M1B S5 (doc 23 §27) -- the pressure of a pen stroke, as a real device
// reports it. Pure: no Excalidraw, no DOM.
//
// A pointer's pressure is a number from 0 to 1, and 0.5 from a device that
// does not measure it. That is what Excalidraw expects (it keeps the value of
// every point of a stroke in `pressures`, and draws the stroke's width from
// it) and what the board's persistence contract accepts: Subway refuses a
// scene with a pressure outside 0..1.
//
// Some phones do not keep to it. The Android phone of the S5 device run
// reported about 11.5 for every finger touch. A stroke drawn on it was kept
// with those numbers, drawn with a wild width, and -- refused by Subway --
// left the whole board unable to save from then on.
//
// A pressure outside 0..1 is not a measurement. A stroke that has one is
// kept the way Excalidraw itself keeps a stroke from a device with no
// pressure: `simulatePressure: true` and no `pressures` (the width is then
// drawn from the speed of the stroke).

function isUsable(pressure) {
  return (
    typeof pressure === 'number' &&
    Number.isFinite(pressure) &&
    pressure >= 0 &&
    pressure <= 1
  )
}

/**
 * Whether a pointer's pressure is a measurement at all.
 * @param {*} pressure -- `PointerEvent.pressure`
 */
export function isUsablePressure(pressure) {
  return isUsable(pressure)
}

// What a pointer reports when its device does not measure pressure, and what
// Excalidraw reads as "no pressure: simulate it".
export const NO_PRESSURE = 0.5

/**
 * Whether a pen stroke holds a pressure that is not a measurement.
 * @param {object} element -- any board element
 */
export function hasUnusablePressure(element) {
  return (
    !!element &&
    element.type === 'freedraw' &&
    Array.isArray(element.pressures) &&
    element.pressures.some(pressure => !isUsable(pressure))
  )
}

/**
 * What such a stroke becomes: Excalidraw's own form of a stroke without
 * pressure. Its points are untouched.
 * @returns {{pressures: number[], simulatePressure: boolean}}
 */
export function strokeWithoutPressure() {
  return { pressures: [], simulatePressure: true }
}

/**
 * Whether a pen stroke is finished (the pointer is up): Excalidraw gives it
 * its last committed point then. A stroke still being drawn is Excalidraw's
 * to change.
 * @param {object} element
 */
export function isFinishedStroke(element) {
  return (
    !!element && element.type === 'freedraw' && !!element.lastCommittedPoint
  )
}

/**
 * The live scene with every FINISHED stroke that holds an unusable pressure
 * replaced by the same stroke without pressure, so that what is on screen is
 * what is saved.
 * @param {ReadonlyArray<object>} elements
 * @param {function(object, object): object} apply -- the editor's
 *   `newElementWith` (it gives the element its new version)
 * @returns {{elements: ReadonlyArray<object>, changed: boolean}} `elements`
 *   is the SAME array when nothing changed
 */
export function normalizeBoardStrokes(elements, apply) {
  let changed = false
  const next = (elements || []).map(element => {
    if (
      !element ||
      element.isDeleted ||
      !isFinishedStroke(element) ||
      !hasUnusablePressure(element)
    ) {
      return element
    }
    changed = true
    return apply(element, strokeWithoutPressure())
  })
  return changed ? { elements: next, changed } : { elements, changed }
}
